import type { ReviewWhen } from "./domain/reviewQueue";
import type { RouteId } from "./routes";

export const copy = {
  brand: "MediaDesk",
  studio: "小红书 · AI准点下班研究所",
  dash: "—",
  refresh: "刷新",
  goReview: "去审阅",
  doIt: "做",
  skip: "不做",
  approve: "批准发布",
  undo: "撤销",
  close: "关闭",
  openNav: "打开导航",
  collapseNav: "收起侧栏",
  expandNav: "展开侧栏",
  closeTip: "关闭提示",
  search: "搜索选题",
  searchPlaceholder: "搜索选题、角度、批次",
  signOut: "退出登录",
  localPreview: "本地预览",
  signedIn: "已登录",
  offline: "离线，本机数据还在",
  syncing: "同步中",
  waitingSync: "等待第一次同步",
  notionConnected: "Notion 已连接",
  syncError: "同步异常",
  pendingWrites: (count: number) => `${count} 项修改还在等待写回。`,
  devNotice: "当前没有配置 Clerk，这是本地预览。数据只留在这台浏览器，不会上传。",
  noHeatBasis: "暂无热度依据",
  importSoon: "导入数据（即将支持）",
  emptyChart: "暂无数据，导入小红书数据后显示",
  comingSoon: "即将支持",
  nav: {
    groups: {
      topics: "选题组",
      make: "制作组",
      ops: "运营组",
    },
    today: "今天",
    inbox: "选题库",
    research: "爆款研究",
    kanban: "内容看板",
    review: "审稿",
    metrics: "数据",
    settings: "设置",
  },
  badge: {
    today: (count: number) => `${count} 件今天要处理`,
    inbox: (count: number) => `${count} 条待确认选题`,
    board: (count: number) => `${count} 条制作中`,
    publish: (count: number) => `${count} 条待发布`,
    review: (count: number) => `${count} 篇待审阅`,
    tasks: (count: number) => `${count} 条助理任务还在进行`,
    group: (count: number) => `这一组还有 ${count} 件待处理`,
  },
  pages: {
    today: { title: "今天", hint: "先确认选题，再审稿子。" },
    inbox: { title: "选题库", hint: "筛选选题，决定做还是不做。" },
    find: { title: "找选题", hint: "定数量、平台权重和要求，交给热点监控助理。" },
    research: { title: "爆款研究", hint: "用对标笔记和风格规范，让待发的稿子先过网感。" },
    tasks: { title: "任务", hint: "看执行者有没有在做，以及每条任务现在到哪一步。" },
    board: { title: "制作进度", hint: "已确认的选题按阶段往下走。" },
    publish: { title: "发布管理", hint: "按计划日期查看要发的稿子。" },
    notes: { title: "笔记数据", hint: "笔记数据未接入，不显示数字。" },
    metrics: { title: "数据", hint: "连上创作者中心之后，这里显示这个账号的数据。" },
    kanban: { title: "内容", hint: "" },
    review: { title: "审稿", hint: "按手机上的笔记来看，通过之后进入待发布，不会直接发出去。" },
    accounts: { title: "账号管理", hint: "当前只显示小红书这一个账号。" },
    hot: { title: "热点追踪", hint: "未接入，将读取热点监控助理的结果。" },
    assistant: { title: "助理记录", hint: "运行记录未接入，篇数看左侧。" },
    executors: { title: "执行者", hint: "接入能领取任务的程序。不绑定某一家模型。" },
    settings: { title: "设置", hint: "执行者、账号、Notion 和找选题偏好。" },
  },
  home: {
    find: "找选题",
    likes: "昨日新增点赞",
    saves: "昨日新增收藏",
    followers: "昨日新增粉丝",
    week: "本周发布篇数",
    unsynced: "未同步",
    decide: "待确认选题",
    review: "待审稿子",
    reason: "推荐理由",
    viral: "爆款可能性",
    sources: "来源",
    noDetail: "没有更多说明。",
    empty: "今天没有要确认的选题，也没有要审的稿子。点「找选题」，助理会把新的选题排进这里。",
    emptyDecide: "没有待确认的选题。",
    emptyReview: "没有待审的稿子。",
    seeAll: "查看全部",
    goReview: "去审稿",
  },
  lane: {
    calendar: "发布日历",
    segments: { list: "列表", board: "看板", calendar: "日历" },
    soonNone: "没有 24 小时内要发的稿子。",
    empty: "还没有内容。",
    emptyLink: "去选题库",
    emptyStep: "这一步还没有选题。",
    seeAll: "查看全部",
    emptySearch: "没有对得上的选题。",
    clearSearch: "清除搜索",
    title: "标题",
    status: "状态",
    score: "网感分",
    assistant: "负责助理",
    age: "停留",
    plan: "计划发布",
    drag: "拖动排序",
    noTask: "没有正在进行的助理任务。",
    queued: "排队中",
  },
  desk: {
    all: "全部",
    list: "列表",
    board: "看板",
    tasks: "助理任务",
    search: "搜索选题",
    empty: "这一栏没有选题。",
    taskEmpty: "没有正在排队、执行或失败的助理任务。",
    orphan: "还没写回选题",
    column: "状态",
  },
  settingsTabs: {
    executors: "执行者与任务",
    account: "账号",
    notion: "Notion 与导入",
    find: "找选题默认偏好",
  },
  prefsSaved: "已保存找选题偏好。",
  savePrefs: "保存偏好",
  overview: {
    title: "数据概览",
    hint: "先看今天要确认、要审、要看的数。",
    decideTitle: "待确认选题",
    reviewTitle: "待审阅稿子",
    yesterdayTitle: "昨天的数据",
    openReview: "去审稿",
    unsynced: "未同步",
    likes: "新增点赞",
    saves: "新增收藏",
    followers: "新增粉丝",
    unconnected: "未接入",
    seeAll: (count: number) => `查看全部（${count}）`,
    kpis: {
      review: "待审阅",
      pending: "待确认",
      overdue: "已过时间未发",
      due: "今日待发",
    },
    reviewList: "待审阅",
    decide: "待确认选题",
    trend: "近7天笔记数据趋势",
    top: "已录入的帖子",
    week: {
      title: "未来 7 天排期",
      empty: "可安排",
      summary: (posts: number, emptyDays: number) => `7 天内 ${posts} 篇已排期，${emptyDays} 天空缺`,
      today: (weekday: string, monthDay: string) => `今天 / 周${weekday} ${monthDay}`,
      tomorrow: (weekday: string, monthDay: string) => `明天 / 周${weekday} ${monthDay}`,
      later: (weekday: string, monthDay: string) => `周${weekday} ${monthDay}`,
    },
    emptyReview: "没有待审阅的选题。",
    reviewSummary: (total: number, soon: number) =>
      soon > 0 ? `${total} 篇待审阅，其中 ${soon} 篇 24 小时内要发` : `${total} 篇待审阅`,
    emptyDecide: "没有待确认的选题。",
    columns: {
      title: "标题",
      audience: "目标人群",
      source: "来源",
      heat: "热度",
      status: "状态",
      plan: "计划时间",
      actions: "操作",
    },
    delta: (value: number) => (value > 0 ? `较上周 +${value}` : value < 0 ? `较上周 ${value}` : "与上周持平"),
  },
  library: {
    all: "全部",
    empty: "这个状态下没有选题。",
    back: "查看全部选题",
    open: "打开",
  },
  error: {
    title: "这一页没有显示出来",
    body: "出了点问题。刷新后通常就能恢复。",
    reload: "重新加载",
    retry: "再试一次",
    section: "这一块没有显示出来",
  },
  progress: {
    lead: "你确认要做的选题会在这里排队，小红书内容助理按顺序制作；制作完成后进入待审阅。",
    queued: "排队中",
    recent: "最近7天",
    empty: "这一阶段还没有选题。",
    up: "上移",
    down: "下移",
    saved: "已调整队列顺序，正在写回。",
  },
  decide: {
    hint: "J / K 移动，D 做，X 不做",
    all: "全选",
    selected: (count: number) => `已选 ${count} 篇`,
    batch: (count: number, status: string) => `已将 ${count} 篇标为${status}`,
  },
  account: {
    current: "AI准点下班研究所 · 小红书",
    soon: "即将支持",
    add: "添加账号",
    switchLabel: "切换账号",
    rename: "抖音账号名称",
    renameHint: "只改工作台里的显示名。",
    others: "其他平台还没有独立工作区。",
    offline: "未连接（需要助理登录创作者中心）",
    connectHow: "助理会登录创作者中心，你扫一次二维码。连上之后，这里才有粉丝、浏览和帖子。",
    connectTitle: "这个账号还没连接",
    connectAction: "让助理去登录创作者中心",
    saved: (name: string) => `已添加「${name}」。`,
  },
  tasks: {
    queued: "已加入任务队列。",
    empty: "还没有任务。",
    emptyLead: "三步就能让助理去搜集选题。",
    steps: ["提交任务，写清数量、平台和要求", "助理领取，并按平台权重去搜集", "结果按推荐排名进入待确认"],
    columns: { type: "类型", status: "状态", platforms: "平台", created: "创建时间", result: "结果" },
    running: "有进行中的任务",
    noExecutor: "还没有接入执行者",
    rewake: "重新唤醒",
  },
  executors: {
    intro: "任何按 docs/agent-protocol.md 对接的程序都可以当执行者，例如 Grok Bot 助理、自己的脚本、n8n，或其他 AI 助理。工作台不绑定某一家模型。",
    local: "本地预览接不到服务器。要生成密钥、保存唤醒地址，请用已登录的预览。",
    empty: "还没有执行者。添加之后，新任务会按类型去唤醒它。",
    open: "去接入执行者",
    add: "添加执行者",
    save: "保存",
    cancel: "取消",
    edit: "编辑",
    disable: "停用",
    enable: "启用",
    test: "发送测试唤醒",
    regen: "重新生成密钥",
    regenWarn: "旧密钥会立刻失效。新密钥只显示这一次。",
    keyOnce: "密钥只显示这一次。请复制保存，离开之后只能看到前后几位。",
    copyKey: "复制",
    copied: "已复制。",
    name: "名称",
    wakeUrl: "唤醒地址",
    headerName: "唤醒请求头",
    headerValue: "请求头的值",
    headerHint: "可选。名称例如 Authorization，值例如 Bearer xxx。保存后不再回显，只显示已保存。",
    headerSaved: "已保存",
    types: "负责的任务类型",
    noHeader: "没有额外请求头",
    errors: {
      name: "先写执行者的名称。",
      url: "唤醒地址需要以 https:// 开头。",
      header: "请求头要成对填写名称和值，名称只用字母、数字和短横线。",
      types: "至少选一种负责任务。",
    },
  },
  find: {
    open: "找选题",
    title: "找新选题",
    platforms: "平台和权重",
    all: "全选",
    direction: "要求",
    keyword: "方向、关键词或时间范围，例如：AI 提效，最近7天",
    count: "找多少条",
    weight: "权重",
    sum: "合计 100%",
    submit: "交给助理",
    cancel: "取消",
    recent: "最近的搜集",
    recentEmpty: "提交之后，结果会按推荐排名出现在下面，并进入选题库的待确认。",
    noResult: "还没有写回选题。",
    seeInbox: "在选题库查看",
  },
  research: {
    tabs: { notes: "对标笔记库", style: "账号风格规范", score: "发前打分" },
    seen: "这些互动数是记录当天看到的，不是实时数据。",
    findCount: "找多少条",
    keywords: "关键词",
    keywordPlaceholder: "例如：AI 办公、一周复盘、提示词",
    findSubmit: "请助理找对标爆款",
    add: "手动添加",
    cancel: "取消",
    saveNote: "保存笔记",
    emptyNotes: "还没有对标笔记。可以手动记一条，或请助理去找。",
    recent: "最近的查找",
    recorded: "记录于",
    breakdown: "拆解",
    breakdownDone: "已拆解",
    saveBreakdown: "保存拆解",
    askBreakdown: "请助理拆解",
    needTitle: "先写标题。",
    needDate: "先填记录日期。互动数对应这一天，不能空着。",
    badLink: "链接需要以 http:// 或 https:// 开头。",
    badCover: "封面链接需要以 https:// 开头。",
    badCount: "互动数留空，或填 0 以上的整数。",
    styleSave: "保存为新版本",
    styleEmpty: "还没有风格规范。写好并保存后，小红书内容助理写稿前会读当前这一版。",
    history: "版本记录",
    current: "当前",
    useCurrent: "用作当前",
    pass: "达标线",
    passHint: "满分 40",
    savePass: "保存达标线",
    emptyScore: "没有待审阅的稿子。稿子进入待审阅后，可以请助理打分。",
    sense: "网感分",
    unscored: "尚未打分",
    askScore: "请助理打分",
    below: "未达标，建议退回重写",
    rewrite: "退回重写",
    noGuide: "打分时还没有风格规范。",
    against: "对照的风格规范",
    fields: {
      link: "链接",
      platform: "平台",
      title: "标题",
      author: "作者",
      likes: "点赞",
      collects: "收藏",
      comments: "评论",
      recorded: "记录日期",
      cover: "封面图片链接",
      note: "备注",
      titlePattern: "标题套路",
      coverType: "封面类型",
      bodyStructure: "正文结构",
      aiTools: "涉及的AI工具",
      interaction: "互动点",
      whyViral: "为什么能火",
      titleFormula: "标题公式",
      coverLayout: "封面版式",
      voice: "语气与人设",
      aiPractice: "每篇必须有的AI实操点",
      banned: "禁用词/禁用写法",
      goodTitles: "示例好标题",
      badTitles: "示例坏标题",
    },
    subs: { sense: "网感", ai: "AI相关度", save: "收藏价值", cover: "封面吸引力" },
  },
  recommend: {
    rank: "推荐排名",
    reason: "推荐理由",
    viral: "爆款可能性",
    links: "信息来源",
    platform: "来源平台",
    expand: "展开理由",
    collapse: "收起",
    task: "这次任务找到的",
    allTasks: "全部任务",
  },
  generate: {
    menu: "生成",
  },
  metricsDouyin: {
    sync: "让助理同步创作者中心数据",
    import: "导入创作者中心导出文件",
    hint: "同步或导入创作者中心数据。",
    lead: "抖音个人号没有官方接口。可以让助理去创作者中心同步，或导入导出的表格。",
  },
  assistants: {
    title: "助理状态",
    making: (count: number) => `制作中 ${count} 篇`,
    latest: (title: string, when: string) => `最近选题：${title}${when ? ` · ${when}` : ""}`,
    empty: "还没有这个助理加入的选题",
    sync: (when: string) => `选题库同步 ${when}`,
    note: "只统计选题库里的来源。",
  },
  publish: {
    queue: "待发布",
    empty: "没有待发布的选题。",
  },
  today: {
    review: "待审阅",
    decide: "待确认",
    due: "今日待发",
    done: "今天的事处理完了",
    emptyReview: "没有待审阅的稿子。",
    emptyDecide: "没有待确认的选题。",
    emptyDue: "今天没有待发的稿子。",
    finish: "标为已完成",
    open: "打开",
    now: "现在",
  },
  importMap: {
    title: "导入小红书后台导出文件（CSV/Excel）",
    lead: "上传后台导出的表格。只认下面这些列，认不出的列会跳过。",
    rows: ["日期 → 记录日期", "粉丝、浏览、点赞、收藏、评论、发帖数 → 账号当日", "标题、链接 → 单条帖子"],
  },
  flow: {
    pass: "通过",
    editPass: "编辑后通过",
    confirmPass: "确认通过",
    cancelEdit: "取消编辑",
    returnReason: "退回重写",
    confirmReturn: "确认退回",
    reasonNeeded: "先写一句修改意见。",
    coverMissing: "封面未生成",
    bodyMissing: "正文还没读到。",
    localDraft: "本地预览读不到 Notion 正文。登录后的预览可以打开稿子。",
    localWrite: "已标为待发布。本地预览写不回 Notion 正文。",
    noAssistant: "未指定",
    stage: "在这一步",
    mapping: "待确认留在待确认。已确认和制作中都算制作中。待审阅、待发布各自一列。已完成显示为已发布。不做不出现。",
    accountOnly: "当前工作台只显示小红书账号。其他平台的添加入口先收起，数据还在。",
    emptyColumn: "这一列是空的。",
    comment: "修改意见",
    regenerate: "按建议重新生成",
    evaluation: "我的评价",
    suggestion: "修改建议",
    targets: "这条建议针对",
    waiting: "任务已创建，等待执行者领取",
    migration: "任务已创建。完整审阅记录要等迁移 0008 应用到数据库之后才能保存。",
    localRewrite: "已在本地记下这一版。本地预览写不回 Notion。",
    title: "标题",
    body: "正文",
    tags: "标签",
  },
  placeholders: {
    notes: "未接入",
    hot: "未接入",
    assistant: "未接入",
  },
  fields: {
    source: "来源助理",
    reason: "推荐理由",
    heat: "热度",
    heatBasis: "热度依据",
    reference: "参考链接",
    queue: "队列顺序",
    actual: "实际发布时间",
  },
  status: {
    confirmed: (title: string) => `「${title}」标为已确认`,
    skipped: (title: string) => `「${title}」标为不做`,
    approved: (title: string) => `「${title}」标为待发布`,
    returned: (title: string) => `「${title}」已退回制作中`,
  },
  schedule: {
    unscheduled: "未排期",
    overdue: "已过时间",
    soon: "24小时内",
    today: (time: string) => (time ? `今天 ${time} 发` : "今天 发"),
    tomorrow: (time: string) => (time ? `明天 ${time} 发` : "明天 发"),
    dated: (weekday: string, monthDay: string, time: string) =>
      time ? `周${weekday} ${monthDay} ${time} 发` : `周${weekday} ${monthDay} 发`,
  },
  review: {
    open: "审阅",
    eyebrow: "审阅稿件",
    loading: "正在读取 Notion 正文…",
    emptyBody: "这篇稿子的正文是空的。",
    titleOnly: "目前只有标题。",
    script: "口播稿",
    notes: "方法与备注",
    tags: "标签",
    cover: "封面",
    notion: "在 Notion 中打开",
    return: "退回修改",
    returnConfirm: "确认退回",
    cancelReturn: "取消",
    commentLabel: "修改意见",
    commentPlaceholder: "写一句要改的地方",
    commentRequired: "先写一句修改意见。",
    previewBlocked: "本地预览读不到 Notion 正文。登录后的预览可以打开稿子。",
    failed: "稿子没有读出来。",
    imageFailed: "这张图的链接过期了，关掉再打开会重新取。",
  },
} as const;

export function formatScheduleDay(relative: "today" | "tomorrow" | "later", weekday: string, monthDay: string): string {
  if (relative === "today") return copy.overview.week.today(weekday, monthDay);
  if (relative === "tomorrow") return copy.overview.week.tomorrow(weekday, monthDay);
  return copy.overview.week.later(weekday, monthDay);
}

export function formatReviewWhen(when: Pick<ReviewWhen, "kind" | "time" | "weekday" | "monthDay">): string {
  if (when.kind === "unscheduled") return copy.schedule.unscheduled;
  if (when.kind === "today") return copy.schedule.today(when.time ?? "");
  if (when.kind === "tomorrow") return copy.schedule.tomorrow(when.time ?? "");
  return copy.schedule.dated(when.weekday, when.monthDay, when.time ?? "");
}

export function navLabel(id: RouteId): string {
  return copy.nav[id];
}

export function itemBadge(count: number, tip: string): { count: number; tip: string } | null {
  if (count <= 0) return null;
  return { count, tip };
}
